/**
 * Best-effort client-side downscale/compression before upload so phones don't
 * push 12 MP originals over mobile data. The server re-processes everything
 * regardless (this is a bandwidth optimisation, never a security boundary).
 */
export type ResizeOptions = { maxDimension?: number; quality?: number; mimeType?: 'image/jpeg' | 'image/webp' }

export async function resizeImageForUpload(file: File, options: ResizeOptions = {}): Promise<File> {
  const maxDimension = options.maxDimension ?? 1600
  const quality = options.quality ?? 0.85
  const mimeType = options.mimeType ?? 'image/jpeg'
  if (!file.type.startsWith('image/')) return file

  try {
    // createImageBitmap honours EXIF orientation in modern browsers.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions)
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
      bitmap.close()
      return file
    }
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return file
    }
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, quality))
    if (!blob) return file
    const ext = mimeType === 'image/webp' ? 'webp' : 'jpg'
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + `.${ext}`, { type: mimeType })
  } catch {
    return file
  }
}
